import { FC, useMemo, useState } from 'react';

// Third-party
import { Card, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid2';
import Divider from '@mui/material/Divider';
import moment from 'moment';

// Components
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Step } from 'react-joyride';
import { alpha } from '@mui/material/styles';
import { bgBlur, BASE_COLORS } from '@mgm/common/utils';
import { BaseDrawerTitle } from '@mgm/common/components/base';
import { ClockActions } from './ClockActions';
import { ClockShifts } from './ClockShifts';
import { Timer } from './Timer';
import { BreakTimer } from './BreakTimer';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';

// Redux-toolkit
import { getShiftDetails } from '../../../store/clock/selectors';
import { sheetsActions } from '../../../store/sheets/slice';
import { getSheets } from '../../../store/sheets/selectors';
import { getAuthenticateUser } from '../../../store/authenticate/selectors';

// Utils

// Types
import { TClockNav } from '../type';
import { TourGuide } from '../../../components';

const ClockNavigation: FC<TClockNav> = ({ isOpenNav, handleClose }) => {
  const theme = useTheme();
  const { t } = useTranslation(['clock', 'tour', 'common']);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const shiftDetails = useAppSelector(getShiftDetails);
  const sheets = useAppSelector(getSheets);
  const { personnel } = useAppSelector(getAuthenticateUser);
  const [runTour, setRunTour] = useState(false);

  const tourSteps: Array<Step> = [
    {
      target: '#select-location',
      disableBeacon: true,
      content: t('selectTheLocationClockIn', { ns: 'tour' }),
    },
    {
      target: '#clock-in',
      disableBeacon: true,
      content: t('thisButtonAllowsYouToClock', { ns: 'tour' }),
    },
    {
      target: '#total-work',
      disableBeacon: true,
      content: t('hereYouWillSeeYourCurrentShift', { ns: 'tour' }),
    },
    {
      target: '#current-period',
      disableBeacon: true,
      content: t('thisSectionDisplaysAllYourShifts', { ns: 'tour' }),
    },
    {
      target: '#view-all',
      disableBeacon: true,
      content: t('clickHereToViewYourCompleteSheets', { ns: 'tour' }),
    },
  ];

  const totalTime = useMemo(() => {
    const time = shiftDetails.totalTime || null;

    if (!time) return '00:00:00';
    return moment().startOf('day').second(time).format('HH:mm:ss');
  }, [shiftDetails.totalTime]);

  const totalBreakTime = useMemo(() => {
    const breakTime = shiftDetails.breakTime || null;

    if (!breakTime) return '00:00:00';
    return moment().startOf('day').second(breakTime).format('HH:mm:ss');
  }, [shiftDetails.breakTime]);

  const navTitle = useMemo(() => (!shiftDetails.started ? t('letsGetToWork') : t('youAreClockedIn')), [shiftDetails.started]);

  const openAllShifts = () => {
    const chosenMember = sheets.find((sheet) => sheet.personnel.email === personnel.email);

    if (chosenMember) {
      dispatch(sheetsActions.setSelectedMember(chosenMember));
      navigate(`timesheets/:${chosenMember.personnel?.name}`);
    }
  };

  const handleStartTour = () => {
    setRunTour(true);
  };

  return (
    <Drawer
      anchor="right"
      open={isOpenNav}
      transitionDuration={{ enter: 500, exit: 500 }}
      onClose={handleClose}
      BackdropProps={{ invisible: true }}
      PaperProps={{
        sx: {
          ...bgBlur({ color: theme.palette.background.default, opacity: 0.9 }),
          width: isMobile ? '100%' : 600,
          boxShadow: `-24px 12px 40px 0 ${alpha(
            theme.palette.mode === 'light' ? theme.palette.grey[500] : theme.palette.common.black,
            0.16,
          )}`,
        },
      }}
    >
      <BaseDrawerTitle
        handleStartTour={handleStartTour}
        isDisplayHelp
        handleClose={handleClose}
        tooltipText={t('clockHereToStartAGuided', { ns: 'common' })}
        title={navTitle}
      />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <TourGuide steps={tourSteps} run={runTour} onFinish={() => setRunTour(false)} />
      <Stack
        paddingBottom={2}
        paddingTop={2}
        overflow="auto"
        sx={{
          paddingX: { xs: 3, lg: 5, md: 5 },
        }}
        spacing={4}
      >
        <ClockActions closeNav={handleClose} />
        <Card
          id="total-work"
          variant="outlined"
          sx={{
            minHeight: 150,
          }}
        >
          <CardHeader
            sx={{
              p: 2,
            }}
            titleTypographyProps={{
              textAlign: 'center',
              variant: 'body1',
            }}
            title={t('totalWorkingHour')}
          />
          <Divider sx={{ borderStyle: 'dashed' }} />
          <CardContent>
            <Grid container>
              <Grid
                size={{
                  lg: 6, md: 6, sm: 6, xs: 6,
                }}
              >
                {shiftDetails?.inBreak ? <BreakTimer /> : <Timer />}
              </Grid>
              <Grid size={{
                lg: 6, md: 6, sm: 6, xs: 6,
              }}
              >
                <Typography
                  sx={{
                    color: 'text.secondary',
                  }}
                  variant="body2"
                >
                  {shiftDetails?.inBreak ? t('shiftBreakTime') : t('dailyWorkTime')}
                </Typography>
                <Typography paddingTop={1} variant="caption">
                  {shiftDetails?.inBreak ? totalBreakTime : totalTime}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <div id="current-period">
          <Box
            justifyContent="space-between"
            display="flex"
            alignItems="center"
          >
            <div>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('currentPeriod')}
              </Typography>
              <Typography paddingTop={1} fontSize={14}>
                {`${moment().startOf('month').format('MMM Do')} - ${moment().endOf('month').format('MMM Do')}`}
              </Typography>
            </div>
          </Box>
          <ClockShifts />
        </div>
      </Stack>
      <Divider sx={{ borderStyle: 'dashed' }} />
      <Box
        id="view-all"
        sx={{
          bottom: 0, left: 0, right: 0,
        }}
      >
        <BottomNavigation
          showLabels
        >
          <BottomNavigationAction
            onClick={openAllShifts}
            disableRipple
            sx={{
              fontSize: 16,
              color: 'text.primary',
              ':hover': {
                color: BASE_COLORS.primary,
              },
            }}
            label={t('viewAll')}
          />
        </BottomNavigation>
      </Box>
    </Drawer>
  );
};

export {
  ClockNavigation,
};
